import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useSession } from '../../state/useSession';
import { trackPixel } from '../../analytics/metaPixel';
import { returnTrip } from '../../shell/returnTo';
import { Button, Screen, TextField } from '../../ui';
import { takePassword } from './heldPassword';
import { IdeaQuote, ideaFrom } from './JourneyIdea';
import ReportProblem from '../../shell/ReportProblem';
import { funnelStep, useFunnelStep } from '../../analytics/funnel';

/** Cognito's sign-up codes are six digits; the field confirms by itself at the sixth. */
const CODE_LENGTH = 6;

/**
 * How long Resend waits after a code is sent. Cognito stops sending after a few codes in a
 * row, and each new one makes the last one useless, so a Resend pressed while the first email
 * is still on its way costs the person both codes.
 */
export const RESEND_COOLDOWN_MS = 30_000;

/**
 * What Confirm is handed: the email, where the person was going, and — from Sign in's "not
 * confirmed" and Sign up's "already exists" — `resend`, because the code from last time may be
 * long gone and a fresh one is what they need.
 */
export interface ConfirmState {
  email?: string;
  from?: string;
  resend?: boolean;
  /**
   * When Sign up's code was sent (Date.now()), so Resend waits from then. In history state, so a
   * reload keeps the wait rather than handing out a second code at once.
   */
  sentAt?: number;
}

/**
 * Whether Cognito refused because the account is ALREADY confirmed — to a resend ("User is already
 * confirmed.", InvalidParameterException) or to a code ("… Current status is CONFIRMED",
 * NotAuthorizedException). Read from the message, because both codes also mean other things: an
 * InvalidParameterException for a malformed address must not send anybody to Sign in.
 */
function alreadyConfirmed(err: unknown): boolean {
  const message = err instanceof Error ? err.message : '';
  return /already confirmed|current status is confirmed/i.test(message);
}

export default function Confirm() {
  useFunnelStep('code_viewed');
  const { confirmSignUp, resendConfirmation, signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const arrived = location.state as ConfirmState | null;
  const emailFromSignUp = arrived?.email ?? '';
  // Where sign-up was sending them; the sign-in after this finishes the trip (see SignUp).
  const from = arrived?.from;
  // Their idea, still in view after a trip to the inbox: the reason they are typing a code.
  const idea = ideaFrom(from);

  /*
   * Sign up's "already exists" covers a confirmed account too, and its "Confirm your email" sent a
   * code on arrival that Cognito refused with the raw "User is already confirmed." — on a code
   * screen with no way to Sign in (review of F7, 2026-09-30). There is nothing to confirm: Sign in,
   * email filled, still on the way to wherever they were going.
   */
  function toSignIn(to: string): void {
    navigate('/sign-in', { replace: true, state: { email: to, confirmed: true, ...(from ? { from } : {}) } });
  }

  const [email, setEmail] = useState(emailFromSignUp);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * Resend had no busy state, so every press sent another code — and Cognito limits how many it
   * will send, so the fourth press was refused while the first three codes raced each other.
   */
  const [resending, setResending] = useState(false);
  const resendingNow = useRef(false);
  /** Resend is held until then; ticking `now` redraws the countdown once a second. */
  const [resendAt, setResendAt] = useState(() => (arrived?.sentAt ?? 0) + RESEND_COOLDOWN_MS);
  const [now, setNow] = useState(() => Date.now());
  const waiting = resendAt > now;
  useEffect(() => {
    if (!waiting) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [waiting]);
  const waitSeconds = Math.ceil((resendAt - now) / 1000);

  async function resend(to: string = email.trim()): Promise<void> {
    if (resendingNow.current || !to) return;
    resendingNow.current = true;
    setResending(true);
    setError(null);
    setNotice(null);
    try {
      await resendConfirmation(to);
      setNotice(`We sent a new code to ${to}.`);
      const sent = Date.now();
      setNow(sent);
      setResendAt(sent + RESEND_COOLDOWN_MS);
    } catch (err) {
      if (alreadyConfirmed(err)) {
        toSignIn(to);
        return;
      }
      setError(err instanceof Error ? err.message : 'Could not send another code.');
    } finally {
      resendingNow.current = false;
      setResending(false);
    }
  }

  // Sent for them, once, when they came here because the code was never used.
  const resentOnArrival = useRef(false);
  useEffect(() => {
    if (!arrived?.resend || !emailFromSignUp || resentOnArrival.current) return;
    resentOnArrival.current = true;
    void resend(emailFromSignUp);
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * One confirmation at a time: the sixth digit submits by itself, and a press of Confirm while
   * that is in flight must not send the same code twice.
   */
  const confirming = useRef(false);
  /** The last code sent by itself, so a refused one is not retried until it is changed. */
  const autoTried = useRef<string | null>(null);

  function onCode(text: string): void {
    // Digits only, whatever was typed or pasted ("123 456", "123-456"); at most six.
    const digits = text.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digits);
    if (digits.length === CODE_LENGTH && digits !== autoTried.current && email.trim()) {
      autoTried.current = digits;
      void confirm(digits);
    }
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    await confirm(code);
  }

  async function confirm(entered: string): Promise<void> {
    if (confirming.current) return;
    confirming.current = true;
    try {
      await confirmAndGo(entered);
    } finally {
      confirming.current = false;
    }
  }

  async function confirmAndGo(entered: string): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await confirmSignUp(email.trim(), entered.trim());
    } catch (err) {
      if (alreadyConfirmed(err)) {
        toSignIn(email.trim());
        return;
      }
      setError(err instanceof Error ? err.message : 'That code was not accepted.');
      setBusy(false);
      return;
    }
    // Here, not at sign-up: an unconfirmed account is not a registration.
    trackPixel('CompleteRegistration');
    funnelStep('code_confirmed');
    /*
     * Signed in with the password they typed moments ago, and on to where they were going. The
     * funnel used to end on an empty Sign in form here, asking for the email and the password
     * again (F7, 2026-09-30). Held in memory by Sign up or Sign in, and only for this email; a
     * reload drops it, and then — as when this sign-in fails for any reason — Sign in is asked
     * with the email already filled.
     */
    const password = takePassword(email);
    if (password) {
      try {
        await signIn(email.trim(), password);
        const trip = returnTrip(from);
        navigate(trip.to, { replace: true, ...(trip.state ? { state: trip.state } : {}) });
        return;
      } catch {
        /* asked to sign in instead, below */
      }
    }
    setBusy(false);
    navigate('/sign-in', { replace: true, state: { email: email.trim(), ...(from ? { from } : {}) } });
  }

  return (
    <Screen>
      {idea && <IdeaQuote idea={idea} />}
      <h1 className="type-display screen-title">Check your email</h1>
      {/*
        Where the code went, how long it can take, and where it hides: the funnel leaves this page
        for the inbox here, and "Enter the code we sent you" alone left people waiting on the wrong
        address or giving up on a code sitting in spam. The subject is the pool's own
        (infra/lambda/shared/authEmail.ts), which both mailers send.
      */}
      {emailFromSignUp ? (
        <p className="type-body confirm-where">
          We sent a 6-digit code to <strong className="confirm-email">{emailFromSignUp}</strong>.
        </p>
      ) : (
        <p className="type-body confirm-where">Enter your email and the 6-digit code we sent to it.</p>
      )}
      <p className="type-caption confirm-wait">
        It can take a minute to arrive. Look for <strong>“Verify your Saddle account”</strong>, and check your spam
        folder if it isn&rsquo;t in your inbox.
      </p>
      <form onSubmit={submit} noValidate>
        {!emailFromSignUp && (
          <TextField label="Email" type="email" autoComplete="email" value={email} onChangeText={setEmail} />
        )}
        <TextField
          label="Code"
          value={code}
          onChangeText={onCode}
          placeholder="123456"
          autoComplete="one-time-code"
          inputMode="numeric"
          autoFocus={Boolean(emailFromSignUp)}
        />
        {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
        {notice && <p role="status" className="type-caption">{notice}</p>}
        <Button title="Confirm" type="submit" loading={busy} disabled={code.length !== CODE_LENGTH || !email.trim()} />
      </form>
      <Button
        title={waiting && !resending ? `Resend code in ${waitSeconds}s` : 'Resend code'}
        variant="ghost"
        loading={resending}
        disabled={waiting}
        onPress={() => void resend()}
      />
      {/*
        A mistyped address is a code that never arrives. Back to Sign up, still carrying where they
        were going; the account made with the wrong address is never confirmed and does no harm.
      */}
      {emailFromSignUp && (
        <p className="type-caption auth-links confirm-other">
          Wrong email? <Link to="/sign-up" replace state={from ? { from } : undefined}>Use a different email</Link>
        </p>
      )}
      {/* "The code never arrives" is reported by exactly the person who cannot sign in. */}
      <p className="type-caption auth-links"><ReportProblem label="Code not arriving? Report a problem" lastError={error} /></p>
    </Screen>
  );
}
